import { Injectable } from '@angular/core';

import { Consumer, createConsumer, Subscription } from '@rails/actioncable';
import { from, MonoTypeOperatorFunction, Observable, timer } from 'rxjs';
import { filter, map, retry, share, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ActionCableChannel,
  ActionCableChannelType,
} from '../models/action-cable.model';
import { Tenant } from '../models/tenant.model';
import { ConfigService } from './config.service';
import { TokenService } from './token.service';

export const connected = Symbol();
export type Connected = typeof connected;

export const disconnected = Symbol();
export type Disconnected = typeof disconnected;

export class ChannelRejectedError extends Error {}

const BASE_RETRY_DELAY_MS = 2000;
const MAX_RETRY_DELAY_MS = 60000;

const backoffDelay = (retryCount: number): number => {
  const exponentialDelay = Math.min(
    BASE_RETRY_DELAY_MS * Math.pow(2, retryCount - 1),
    MAX_RETRY_DELAY_MS
  );
  const jitter = 0.5 + Math.random();

  return Math.round(exponentialDelay * jitter);
};

@Injectable({
  providedIn: 'root',
})
export class ActionCableService {
  constructor(
    private configuration: ConfigService,
    private tokenService: TokenService
  ) {}

  public subscribeToChannel<T extends ActionCableChannel>(
    channel: T,
    params: any = null
  ): Observable<Connected | Disconnected | ActionCableChannelType<T>> {
    return this.getWsConsumer().pipe(
      switchMap(consumer =>
        this.channelObservable<T>(channel, params, consumer)
      )
    );
  }

  public reconnectionHandler<T>(): MonoTypeOperatorFunction<T> {
    return retry({
      resetOnSuccess: true,
      delay: (error, retryCount) => {
        if (error instanceof ChannelRejectedError) {
          throw error;
        }

        return timer(backoffDelay(retryCount));
      },
    });
  }

  private channelObservable<T extends ActionCableChannel>(
    channel: T,
    params: any,
    consumer: Consumer
  ): Observable<Connected | Disconnected | ActionCableChannelType<T>> {
    return new Observable(s => {
      const acSubscription: Subscription = consumer.subscriptions.create(
        { channel, ...params },
        {
          connected: () => s.next(connected),
          disconnected: () => s.next(disconnected),
          messages_list: data => s.next(data),
          received: data => s.next(data),
          rejected: () =>
            s.error(new ChannelRejectedError(`Websocket ${channel} rejected`)),
        }
      );

      return () => {
        acSubscription.unsubscribe();
      };
    });
  }

  private getWsConsumer(): Observable<Consumer> {
    return this.configuration.tenant.pipe(
      filter(x => x !== null),
      // get auth data for initialising ws connection
      switchMap((tenant: Tenant) =>
        from(this.tokenService.authData).pipe(
          map(authData => ({
            access_token: authData.accessToken,
            uid: authData.uid,
            client: authData.client,
            tenant: tenant.subdomain,
          }))
        )
      ),
      // get connected ws consumer
      switchMap(wsConnectionParams => {
        const wsConnectionParamsString = new URLSearchParams(
          wsConnectionParams
        ).toString();

        return new Observable<Consumer>(s => {
          const consumer = createConsumer(
            `${environment.websocketUrl}?${wsConnectionParamsString}`
          );
          consumer.connect();
          s.next(consumer);
          // disconnect consumer on last unsubscribe
          return () => consumer.disconnect();
        });
      }),
      // one consumer can be shared across multiple channels
      // this tracks #of subscribed channels. If we unsubscribe from all channels, consumer.disconnect() is called
      share()
    );
  }
}
